import { LIST_1093_HAS_ELECTORATES } from '@/apis/elections';
import { useLookupCatCampaigns } from '@components/CampaignAutocomplete';
import { useLookupGroups } from '@components/GroupsFetchAutocomplete';
import { useLookupTags } from '@components/TagsFetchAutocomplete';
import { useLookupUsers } from '@components/UsersFetchAutocomplete';
import {
  useLookupBranches,
  useLookupCampaigns,
  useLookupElections,
  useLookupEvents,
  useEventTypes,
  useLookupLists,
  useLookupGeometries,
  useLookupMailing,
} from '@/criteriaLookups';
import { useMemo } from 'react';

import { AllPlaceholderKeys } from '@components/LoadSearchTemplateDialog/types';

import { buildCoreAndMembershipControls } from './controls/coreAndMembershipControls';
import { buildLabelsDatesAndMiscControls } from './controls/labelsDatesAndMiscControls';
import { buildRosterAndEngagementControls } from './controls/rosterAndEngagementControls';
import { buildTaskEventAndCallingControls } from './controls/taskEventAndCallingControls';
import {
  getCallingPeriodQuantityInitialValue,
  getMatchedInitialValue,
  getSimpleInitialValue,
} from './initialValues';
import type { MappedTemplateFormControlMapDeps, TemplateFormControlMap } from './types';

type UseMappedTemplateFormControlMapParams = {
  filterNameAndField: AllPlaceholderKeys;
  initialFlatSearchTreeNode?: MappedTemplateFormControlMapDeps['initialFlatSearchTreeNode'];
  initialValue?: MappedTemplateFormControlMapDeps['initialValue'];
};

export function useMappedTemplateFormControlMap({
  filterNameAndField,
  initialFlatSearchTreeNode,
  initialValue,
}: UseMappedTemplateFormControlMapParams): TemplateFormControlMap {
  const simpleInitialValue = useMemo(
    () => getSimpleInitialValue(initialFlatSearchTreeNode, filterNameAndField),
    [initialFlatSearchTreeNode, filterNameAndField]
  );

  const callingPeriodQuantityInitialValue = useMemo(
    () => getCallingPeriodQuantityInitialValue(initialFlatSearchTreeNode),
    [initialFlatSearchTreeNode]
  );

  const matchedInitialValue = useMemo(
    () => getMatchedInitialValue(filterNameAndField, simpleInitialValue),
    [simpleInitialValue, filterNameAndField]
  );

  const lookupGeometries = useLookupGeometries();
  const lookupTags = useLookupTags();
  const lookupGroups = useLookupGroups();
  const lookupLists = useLookupLists();
  const lookupBranches = useLookupBranches();
  const lookupElections = useLookupElections();
  const lookupElectionsOnlyWithElectorates = useLookupElections(LIST_1093_HAS_ELECTORATES);
  const lookupMailings = useLookupMailing();
  const lookupCatCampaigns = useLookupCatCampaigns();
  const lookupEvents = useLookupEvents();
  const lookupCampaigns = useLookupCampaigns();
  const lookupUsers = useLookupUsers();
  const lookupEventTypes = useEventTypes();

  return useMemo(() => {
    const deps: MappedTemplateFormControlMapDeps = {
      lookupGeometries,
      lookupGroups,
      lookupLists,
      lookupTags,
      lookupBranches,
      lookupMailings,
      lookupCatCampaigns,
      lookupElections,
      lookupElectionsOnlyWithElectorates,
      lookupEvents,
      lookupCampaigns,
      lookupEventTypes,
      lookupUsers,
      initialFlatSearchTreeNode,
      initialValue,
      simpleInitialValue,
      matchedInitialValue,
      callingPeriodQuantityInitialValue,
    };

    return {
      ...buildCoreAndMembershipControls(deps),
      ...buildTaskEventAndCallingControls(deps),
      ...buildRosterAndEngagementControls(deps),
      ...buildLabelsDatesAndMiscControls(deps),
    };
  }, [
    lookupGeometries,
    lookupGroups,
    lookupLists,
    lookupTags,
    lookupBranches,
    lookupMailings,
    lookupCatCampaigns,
    lookupElections,
    lookupElectionsOnlyWithElectorates,
    lookupEvents,
    lookupCampaigns,
    lookupEventTypes,
    lookupUsers,
    initialFlatSearchTreeNode,
    initialValue,
    simpleInitialValue,
    matchedInitialValue,
    callingPeriodQuantityInitialValue,
  ]);
}
